import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { ClinicProgram, clinicProgramService } from '@/services/clinicProgramService'
import { ImageUpload } from '@/components/ui/ImageUpload'

interface ClinicProgramImageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: string
  program: ClinicProgram
  onSuccess: () => void
}

const ClinicProgramImageDialog = ({
  open,
  onOpenChange,
  clinicId,
  program,
  onSuccess,
}: ClinicProgramImageDialogProps) => {
  const { toast } = useToast()
  const [isSaving, setIsSaving] = useState(false)
  const [imageKey, setImageKey] = useState<string | null>(program.image_key || null)
  const [imageUrl, setImageUrl] = useState<string | null>(program.image_url || null)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (open) {
      setImageKey(program.image_key || null)
      setImageUrl(program.image_url || null)
      setHasChanges(false)
    }
  }, [open, program.image_key, program.image_url])

  const handleImageUploaded = (key: string) => {
    setImageKey(key)
    setHasChanges(true)
  }

  const handleImageRemoved = () => {
    setImageKey(null)
    setImageUrl(null)
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!hasChanges) {
      onOpenChange(false)
      return
    }

    try {
      setIsSaving(true)
      await clinicProgramService.updateImage(clinicId, program.id, imageKey)
      toast({
        title: 'Success',
        description: 'Program image updated successfully',
        variant: 'success',
      })
      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to update program image',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Program Image</DialogTitle>
          <DialogDescription>
            Upload or change the image for "{program.title}".
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <ImageUpload
            imageUrl={imageUrl}
            imageKey={imageKey}
            folder="programs"
            onImageUploaded={handleImageUploaded}
            onImageRemoved={handleImageRemoved}
            disabled={isSaving}
          />
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ClinicProgramImageDialog
